<template>
  <Modal
    :modal-types="[LINK_TO_CHART_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-share"
    modal-title="Share Chart"
  >
    <div v-show="isInitializingModal" class="loading-spinner-container">
      <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
    </div>
    <div v-show="!isInitializingModal && failedToCreateLinkToChart" class="failed-to-load-container">
      <div class="error-message">
        Failed to create chart URL link. Please try again.
      </div>
      <button
        type="button"
        class="sc3-button sc3-button-green"
        @click="generateLinkToChart()"
      >
        <i class="fas fa-redo"></i>
        &nbsp;Retry
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </div>
    <div v-show="!isInitializingModal && !failedToCreateLinkToChart" class="link-to-chart-ui-container">
      <p class="info">The link below can be bookmarked, saved, or shared with others</p>
      <input
        type="text"
        v-model="linkToChart"
        class="sc3-text-input"
        autocomplete="off"
      />
      <hr class="sc3-form-divider" />
      <button
        type="button"
        class="sc3-button sc3-button-green"
        @click="copyToClipboard()"
      >
        <i class="fas fa-fw fa-copy"></i>
        &nbsp;Copy Link
      </button>
      <button
        type="button"
        class="sc3-button sc3-button-light-grey"
        @click="reloadWithLink()"
      >
        <i class="fas fa-fw fa-sync"></i>
        &nbsp;Reload with Link
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useModals } from '@/composables/useModals';
  import { usePermalink } from '@/composables/usePermalink';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const { modalType } = storeToRefs(layoutStore);
  const {
    LINK_TO_CHART_MODAL,
    closeModal
  } = useModals();
  const { getLinkableVersionLong, getShortUrl } = usePermalink();
  const { addGreenActionResultMessage, addRedActionResultMessage } = useActionResultMessages();
  const longLinkToChart = ref('');
  const linkToChart = ref('');
  const failedToCreateLinkToChart = ref(false);
  const isInitializingModal = ref(false);

  async function handleModalTypeChanged() {
    if (modalType.value !== LINK_TO_CHART_MODAL) {
      resetModalState();
      return;
    }

    await generateLinkToChart();
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(linkToChart.value);

      addGreenActionResultMessage('A link to this chart has been copied to your clipboard!');
    } catch (e) {
      window.sc3WorkbenchLogger.error('Failed to copy link to chart to clipboard from LinkToChartModal.', e);

      addRedActionResultMessage('Failed to copy link to clipboard. Please try again.');
    }

    closeModal();
  }

  async function generateLinkToChart() {
    if (
      isInitializingModal.value ||
      (!failedToCreateLinkToChart.value && longLinkToChart.value)
    ) {
      return;
    }

    const { i: styleCacheId, symbol, annotationsRecid, chartType, perfChartSymbolsString, useMainChartSymbolAsBaseline } = layoutStore;

    try {
      resetModalState();

      isInitializingModal.value = true;
      longLinkToChart.value = await getLinkableVersionLong(styleCacheId, symbol, annotationsRecid, chartType, perfChartSymbolsString, useMainChartSymbolAsBaseline);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to create permalink for styleCacheId: ${styleCacheId} and symbol: ${symbol} and annotationsRecid: ${annotationsRecid} and chartType: ${chartType} and perfChartSymbolsString: ${perfChartSymbolsString} and useMainChartSymbolAsBaseline: ${useMainChartSymbolAsBaseline} from LinkToChartModal.`, e);

      failedToCreateLinkToChart.value = true;
      isInitializingModal.value = false;
      return;
    }

    try {
      linkToChart.value = await getShortUrl(longLinkToChart.value);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to create short URL for permalink with long URL: ${longLinkToChart.value} for styleCacheId: ${styleCacheId} and symbol: ${symbol} and annotationsRecid: ${annotationsRecid} and chartType: ${chartType} and perfChartSymbolsString: ${perfChartSymbolsString} and useMainChartSymbolAsBaseline: ${useMainChartSymbolAsBaseline} from LinkToChartModal. Using long URL instead.`, e);

      linkToChart.value = longLinkToChart.value;
    }

    isInitializingModal.value = false;
  }

  function reloadWithLink() {
    if (!longLinkToChart.value) {
      return;
    }

    window.location.href = longLinkToChart.value;
  }

  function resetModalState() {
    isInitializingModal.value = false;
    failedToCreateLinkToChart.value = false;
    longLinkToChart.value = '';
    linkToChart.value = '';
  }

  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .loading-spinner-container,
  .failed-to-load-container,
  .link-to-chart-ui-container {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .info {
    margin-top: 0px;
    font-size: 16px;
    color: #4f5964;
  }

  .error-message {
    text-align: center;
    border: 1px solid #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
    border-radius: 4px;
    margin: 0px 0px 8px 0px;
    line-height: 2;
    font-size: 16px;
    padding: 0px;
  }
</style>
